// Minimal ambient typings + helpers for Chrome's built-in Prompt API
// (window.LanguageModel). Tool calling is gated behind the "AIPromptAPIParams"
// origin trial (production) or chrome://flags (local dev).

export interface LanguageModelTool {
  name: string;
  description: string;
  inputSchema: object;
  execute: (args: Record<string, unknown>) => Promise<string>;
}

export interface LanguageModelExpectation {
  type: 'text' | 'tool-call' | 'tool-response' | 'image' | 'audio';
  languages?: string[];
}

export interface LanguageModelCreateOptions {
  initialPrompts?: { role: 'system' | 'user' | 'assistant'; content: string }[];
  tools?: LanguageModelTool[];
  // Declares the modalities exchanged with the model; tool use needs the
  // 'tool-response' input and 'tool-call' output types.
  expectedInputs?: LanguageModelExpectation[];
  expectedOutputs?: LanguageModelExpectation[];
  signal?: AbortSignal;
  monitor?: (m: EventTarget) => void;
  // Sampling controls (gated by the params origin trial / flags).
  temperature?: number;
  topK?: number;
}

export interface PromptOptions {
  signal?: AbortSignal;
  // JSON Schema the output is constrained to (structured output).
  responseConstraint?: object;
}

export interface LanguageModelSession {
  prompt(input: string, opts?: PromptOptions): Promise<string>;
  promptStreaming(input: string, opts?: PromptOptions): AsyncIterable<string>;
  destroy(): void;
  // Context-window accounting (per the Prompt API). The API automatically drops
  // the oldest turns (keeping the system prompt) when the window overflows.
  contextUsage?: number;
  contextWindow?: number;
}

export type Availability =
  | 'unavailable'
  | 'downloadable'
  | 'downloading'
  | 'available'
  // legacy spellings still seen in some Chrome builds
  | 'after-download'
  | 'no'
  | 'readily';

export interface LanguageModelStatic {
  availability(opts?: unknown): Promise<Availability>;
  create(opts?: LanguageModelCreateOptions): Promise<LanguageModelSession>;
}

declare global {
  interface Window {
    LanguageModel?: LanguageModelStatic;
  }
}

export function getLanguageModel(): LanguageModelStatic | undefined {
  return typeof window !== 'undefined' ? window.LanguageModel : undefined;
}

export function isAvailableStatus(a: Availability): boolean {
  return a === 'available' || a === 'readily';
}

export function isDownloadableStatus(a: Availability): boolean {
  return a === 'downloadable' || a === 'after-download' || a === 'downloading';
}
